import { AuditAction, Permission, type AuditQuery } from '@qualicloud/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  DatePicker,
  Dialog,
  EmptyState,
  Field,
  IconAlertOctagon,
  IconCheckCircle,
  IconDownload,
  IconHistory,
  IconInfo,
  IconSearch,
  IconShieldCheck,
  Input,
  Pagination,
  Select,
  Skeleton,
  Table,
  cn,
  focusRing,
  useToast,
  type TableColumn,
} from '@/design-system';
import { auditApi, queryKeys, saveBlob, toUserMessage } from '@/lib/api';
import type { AuditEvent } from '@/lib/api/types';
import { formatRegulatedDateTime, truncateHash } from '@/lib/format/datetime';
import { AUDIT_ACTION_LABEL } from '@/lib/i18n';
import { useAuth, useOrganizationTimeZone } from '@/providers/auth-provider';
import type { AuditSearch } from '@/routes/search-schemas';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * Trilha de auditoria.
 *
 * A trilha vive em banco DEDICADO, com credencial própria e apenas INSERT e
 * SELECT — nem o administrador da aplicação altera ou apaga um evento. Esta
 * tela é somente leitura por construção: não há mutação nenhuma além de
 * verificar a cadeia (que grava um relatório de verificação, não um evento de
 * negócio) e exportar (que é auditado).
 *
 * O DIFF visual de `oldValue`/`newValue` é o que torna a trilha utilizável: um
 * JSON cru numa coluna não responde "o que mudou?" para um auditor.
 */
export function AuditTrailPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as unknown as Partial<AuditQuery>;
  const timeZone = useOrganizationTimeZone();
  const { can } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [exporting, setExporting] = useState(false);

  const query: Partial<AuditQuery> = useMemo(
    () => ({
      page: search.page ?? 1,
      pageSize: search.pageSize ?? 50,
      entityType: search.entityType,
      entityId: search.entityId,
      actorUserId: search.actorUserId,
      action: search.action,
      occurredFrom: search.occurredFrom,
      occurredTo: search.occurredTo,
      onlySigned: search.onlySigned ?? false,
    }),
    [search],
  );

  const update = (patch: Partial<AuditQuery>) => {
    void navigate({
      to: '/auditoria',
      search: { ...query, ...patch, page: patch.page ?? 1 } as AuditSearch,
    });
  };

  const eventsQuery = useQuery({
    queryKey: queryKeys.audit.events(query),
    queryFn: () => auditApi.events(query),
    placeholderData: keepPreviousData,
  });

  const chainQuery = useQuery({
    queryKey: queryKeys.audit.chainStatus(),
    queryFn: () => auditApi.chainStatus(),
    staleTime: 60_000,
  });

  const verify = useMutation({
    mutationFn: () => auditApi.verifyChain(),
    onSuccess: async (report) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.audit.chainStatus() });
      toast({
        tone: report.status === 'VALID' ? 'success' : 'error',
        title:
          report.status === 'VALID'
            ? 'Cadeia de integridade íntegra'
            : 'Divergência na cadeia de integridade',
        description:
          report.status === 'VALID'
            ? `${report.eventsVerified} eventos verificados em ${report.durationMs} ms. Nenhuma divergência encontrada.`
            : 'A verificação encontrou divergência entre o hash calculado e o hash armazenado. Comunique imediatamente o responsável pela qualidade.',
        duration: report.status === 'VALID' ? 6000 : 0,
      });
    },
    onError: (error) => {
      toast({
        tone: 'error',
        title: 'Não foi possível verificar a cadeia',
        description: toUserMessage(error),
      });
    },
  });

  const handleExport = async (format: 'csv' | 'json') => {
    setExporting(true);
    try {
      const file = await auditApi.export(query, format);
      saveBlob(file.blob, file.fileName);
      toast({
        tone: 'success',
        title: 'Exportação concluída',
        description:
          'A própria exportação foi registrada na trilha, com a impressão digital do arquivo gerado.',
      });
    } catch (error) {
      toast({ tone: 'error', title: 'Falha na exportação', description: toUserMessage(error) });
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<Array<TableColumn<AuditEvent>>>(
    () => [
      {
        id: 'occurredAt',
        header: 'Data e hora',
        width: '16%',
        cell: (row) => (
          <span className="qc-tabular text-content">
            {formatRegulatedDateTime(row.occurredAt, timeZone)}
          </span>
        ),
      },
      {
        id: 'actor',
        header: 'Autor',
        width: '16%',
        cell: (row) => (
          <div className="min-w-0">
            <span className="block truncate text-content">
              {row.actorName ?? row.actorUsername ?? 'Sistema'}
            </span>
            {row.actorIp ? (
              <span className="block truncate font-mono text-2xs text-content-subtle">
                {row.actorIp}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'action',
        header: 'Ação',
        width: '14%',
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-1">
            <Badge tone={actionTone(row.action)} srPrefix="Ação:">
              {AUDIT_ACTION_LABEL[row.action as AuditAction] ?? row.action}
            </Badge>
            {row.vendorAccess ? (
              <Badge tone="warning" size="sm" srPrefix="Origem:">
                Fornecedor
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        id: 'entity',
        header: 'Registro',
        width: '24%',
        cell: (row) => (
          <div className="min-w-0">
            <span className="block truncate text-content">{row.entityLabel ?? row.entityType}</span>
            <span className="block truncate text-2xs text-content-subtle">{row.entityType}</span>
          </div>
        ),
      },
      {
        id: 'signature',
        header: 'Assinado',
        width: '9%',
        align: 'center',
        cell: (row) =>
          row.signatureId ? (
            <span className="inline-flex items-center gap-1 text-success-content">
              <IconShieldCheck size={14} aria-hidden="true" />
              <span className="qc-sr-only">Evento com assinatura eletrônica vinculada</span>
              <span aria-hidden="true" className="text-xs">
                Sim
              </span>
            </span>
          ) : (
            <span className="text-xs text-content-subtle">
              <span aria-hidden="true">—</span>
              <span className="qc-sr-only">Sem assinatura vinculada</span>
            </span>
          ),
      },
      {
        id: 'chainSeq',
        header: 'Sequência',
        width: '9%',
        align: 'right',
        cell: (row) => <span className="qc-tabular text-content-subtle">{row.chainSeq}</span>,
      },
      {
        id: 'actions',
        header: 'Detalhes',
        width: '12%',
        align: 'right',
        cell: (row) => (
          <Button variant="ghost" size="sm" onClick={() => setSelected(row)}>
            Ver alterações
          </Button>
        ),
      },
    ],
    [timeZone],
  );

  return (
    <>
      <PageHeader
        title="Trilha de auditoria"
        description="Registro imutável de todas as ações realizadas no sistema. Armazenada em banco de dados dedicado, sem permissão de alteração ou exclusão para nenhum usuário da aplicação."
        actions={
          can(Permission.AUDIT_EXPORT) ? (
            <>
              <Button
                variant="secondary"
                leadingIcon={<IconDownload size={15} />}
                loading={exporting}
                onClick={() => void handleExport('csv')}
              >
                Exportar CSV
              </Button>
              <Button
                variant="secondary"
                leadingIcon={<IconDownload size={15} />}
                loading={exporting}
                onClick={() => void handleExport('json')}
              >
                Exportar JSON
              </Button>
            </>
          ) : undefined
        }
      />

      <PageBody className="flex flex-col gap-5">
        <ChainStatusCard
          status={chainQuery.data}
          loading={chainQuery.isPending}
          verifying={verify.isPending}
          onVerify={() => verify.mutate()}
          timeZone={timeZone}
        />

        <Card>
          <CardHeader headingLevel={2} title="Filtros" />
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Field label="Tipo de registro" description="Ex.: Document, User, DocumentVersion.">
              <Input
                value={query.entityType ?? ''}
                onChange={(event) => update({ entityType: event.target.value || undefined })}
                leadingIcon={<IconSearch size={15} />}
                placeholder="Todos"
              />
            </Field>

            <Field label="Identificador do registro">
              <Input
                value={query.entityId ?? ''}
                onChange={(event) => update({ entityId: event.target.value || undefined })}
                placeholder="UUID"
                spellCheck={false}
              />
            </Field>

            <Field label="Ação">
              <Select
                ariaLabel="Filtrar por ação"
                value={query.action?.[0] ?? '__all__'}
                onValueChange={(value) =>
                  update({ action: value === '__all__' ? undefined : [value as AuditAction] })
                }
                options={[
                  { value: '__all__', label: 'Todas as ações' },
                  ...Object.values(AuditAction).map((action) => ({
                    value: action,
                    label: AUDIT_ACTION_LABEL[action],
                  })),
                ]}
              />
            </Field>

            <Field label="Autor (identificador)">
              <Input
                value={query.actorUserId ?? ''}
                onChange={(event) => update({ actorUserId: event.target.value || undefined })}
                placeholder="UUID do usuário"
                spellCheck={false}
              />
            </Field>

            <Field label="Período — de">
              <DatePicker
                value={query.occurredFrom ? toIsoDate(query.occurredFrom) : ''}
                onValueChange={(iso) =>
                  update({ occurredFrom: iso ? new Date(`${iso}T00:00:00`) : undefined })
                }
              />
            </Field>

            <Field label="Período — até">
              <DatePicker
                value={query.occurredTo ? toIsoDate(query.occurredTo) : ''}
                onValueChange={(iso) =>
                  update({ occurredTo: iso ? new Date(`${iso}T23:59:59`) : undefined })
                }
              />
            </Field>

            <div className="flex items-end pb-1 xl:col-span-2">
              <Checkbox
                checked={query.onlySigned ?? false}
                onCheckedChange={(checked) => update({ onlySigned: checked || undefined })}
                label="Apenas eventos com assinatura eletrônica"
                description="Restringe a atos assinados conforme 21 CFR Part 11 Subparte C."
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              variant="ghost"
              onClick={() => void navigate({ to: '/auditoria', search: { page: 1 } })}
            >
              Limpar filtros
            </Button>
          </div>
        </Card>

        <div>
          <Table<AuditEvent>
            caption="Eventos da trilha de auditoria, com data e hora no fuso da organização, autor, ação, registro afetado e posição na cadeia de integridade."
            columns={columns}
            rows={eventsQuery.data?.items ?? []}
            rowKey={(row) => row.id}
            loading={eventsQuery.isPending}
            empty={
              <EmptyState
                icon={<IconHistory size={22} />}
                title="Nenhum evento encontrado"
                description="Nenhum evento corresponde aos filtros informados. Amplie o período ou remova filtros para ver mais resultados."
              />
            }
          />

          {eventsQuery.data && eventsQuery.data.total > 0 ? (
            <div className="rounded-b-lg border-x border-b border-border bg-surface">
              <Pagination
                page={eventsQuery.data.page}
                pageSize={eventsQuery.data.pageSize}
                total={eventsQuery.data.total}
                itemNoun="eventos"
                onPageChange={(page) => update({ page })}
                onPageSizeChange={(pageSize) => update({ pageSize, page: 1 })}
              />
            </div>
          ) : null}
        </div>
      </PageBody>

      {selected ? (
        <EventDiffDialog event={selected} timeZone={timeZone} onClose={() => setSelected(null)} />
      ) : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cadeia de integridade
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Indicador da cadeia de hash.
 *
 * Cada evento carrega o hash do anterior; alterar um evento no meio quebra
 * todos os posteriores. Por isso o estado da cadeia é a pergunta que um auditor
 * faz primeiro, e o botão de verificar é acionável na hora, na frente dele.
 *
 * ACESSIBILIDADE: o estado é ÍCONE + TEXTO + cor, nunca só um ponto colorido.
 * Um resultado "BROKEN" é `role="alert"`, porque é a única informação nesta
 * tela que exige ação imediata.
 */
function ChainStatusCard({
  status,
  loading,
  verifying,
  onVerify,
  timeZone,
}: {
  readonly status: import('@/lib/api/types').ChainStatus | undefined;
  readonly loading: boolean;
  readonly verifying: boolean;
  readonly onVerify: () => void;
  readonly timeZone: string;
}) {
  if (loading) {
    return (
      <Card>
        <Skeleton className="h-5 w-64" />
        <Skeleton className="mt-3 h-4 w-96" />
      </Card>
    );
  }

  const lastRun = status?.lastRun ?? null;
  const broken = lastRun?.status === 'BROKEN' || lastRun?.status === 'ERROR';

  return (
    <Card
      className={cn(
        broken && 'border-l-4 border-l-danger',
        lastRun?.status === 'VALID' && 'border-l-4 border-l-success',
      )}
    >
      <div
        {...(broken ? { role: 'alert' as const } : {})}
        className="flex flex-wrap items-start justify-between gap-4"
      >
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-2 text-base font-semibold text-content">
            {broken ? (
              <IconAlertOctagon size={18} aria-hidden="true" className="text-danger-content" />
            ) : lastRun?.status === 'VALID' ? (
              <IconCheckCircle size={18} aria-hidden="true" className="text-success-content" />
            ) : (
              <IconInfo size={18} aria-hidden="true" className="text-content-subtle" />
            )}
            Cadeia de integridade
            {lastRun ? (
              <Badge
                tone={lastRun.status === 'VALID' ? 'success' : 'danger'}
                srPrefix="Resultado da última verificação:"
              >
                {lastRun.status === 'VALID' ? 'Íntegra' : 'Divergência detectada'}
              </Badge>
            ) : (
              <Badge tone="neutral">Nunca verificada</Badge>
            )}
          </h2>

          <p className="mt-1.5 text-sm text-content-muted">
            {status
              ? `${status.eventCount} eventos registrados. Cada evento carrega o hash do anterior; alterar qualquer um deles quebraria toda a cadeia posterior.`
              : 'Não foi possível ler a situação da cadeia.'}
          </p>

          {lastRun ? (
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs sm:grid-cols-[auto_1fr_auto_1fr]">
              <dt className="text-content-subtle">Última verificação</dt>
              <dd className="text-content qc-tabular">
                {formatRegulatedDateTime(lastRun.finishedAt, timeZone)}
              </dd>
              <dt className="text-content-subtle">Eventos verificados</dt>
              <dd className="text-content qc-tabular">{lastRun.eventsVerified}</dd>
              {lastRun.firstDivergentSeq !== null ? (
                <>
                  <dt className="text-content-subtle">Primeira divergência</dt>
                  <dd className="font-semibold text-danger-content qc-tabular">
                    sequência {lastRun.firstDivergentSeq}
                  </dd>
                  <dt className="text-content-subtle">Tipo</dt>
                  <dd className="text-danger-content">{lastRun.divergenceKind}</dd>
                </>
              ) : null}
            </dl>
          ) : null}

          {status?.headHash ? (
            <p className="mt-2 break-all font-mono text-2xs text-content-subtle">
              Hash da ponta: {truncateHash(status.headHash, 12, 12)}
            </p>
          ) : null}
        </div>

        <Button
          variant="secondary"
          leadingIcon={<IconShieldCheck size={15} />}
          loading={verifying}
          loadingLabel="Verificando a cadeia de integridade…"
          onClick={onVerify}
        >
          Verificar agora
        </Button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Diff
// ─────────────────────────────────────────────────────────────────────────────

interface DiffRow {
  readonly field: string;
  readonly before: string;
  readonly after: string;
  readonly kind: 'added' | 'removed' | 'changed' | 'unchanged';
}

function EventDiffDialog({
  event,
  timeZone,
  onClose,
}: {
  readonly event: AuditEvent;
  readonly timeZone: string;
  readonly onClose: () => void;
}) {
  const rows = useMemo(() => buildDiff(event.oldValue, event.newValue), [event]);
  const changed = rows.filter((row) => row.kind !== 'unchanged');

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="lg"
      title="Detalhe do evento de auditoria"
      description={`${AUDIT_ACTION_LABEL[event.action as AuditAction] ?? event.action} em ${
        event.entityLabel ?? event.entityType
      }`}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {/*
        `tabIndex={0}` e nome próprio: este é o ÚNICO diálogo do sistema que é
        leitura pura — não tem campo, não tem ação além de "Fechar". Sem um
        focável no corpo, as setas rolavam o ancestral do elemento focado, e os
        dois únicos focáveis (o X do cabeçalho e o "Fechar" do rodapé) são
        irmãos da área rolável. Quem opera só por teclado não conseguia rolar
        até a tabela de diferenças — justamente a prova documental do sistema,
        com o hash da cadeia e os valores antes/depois.
      */}
      <div
        tabIndex={0}
        role="region"
        aria-label="Detalhe do evento de auditoria"
        className={cn('flex flex-col gap-5 rounded-sm', focusRing)}
      >
        <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-content-subtle">Data e hora</dt>
          <dd className="text-content qc-tabular">
            {formatRegulatedDateTime(event.occurredAt, timeZone)}
          </dd>
          <dt className="text-content-subtle">Autor</dt>
          <dd className="text-content">
            {event.actorName ?? event.actorUsername ?? 'Sistema'}
            {event.vendorAccess ? (
              <Badge tone="warning" size="sm" className="ml-2">
                Acesso de fornecedor
              </Badge>
            ) : null}
          </dd>
          <dt className="text-content-subtle">Origem</dt>
          <dd className="font-mono text-xs text-content">{event.actorIp ?? '—'}</dd>
          <dt className="text-content-subtle">Justificativa</dt>
          <dd className="text-content">{event.reason ?? '—'}</dd>
          <dt className="text-content-subtle">Assinatura vinculada</dt>
          <dd className="font-mono text-xs text-content">{event.signatureId ?? '—'}</dd>
          <dt className="text-content-subtle">Posição na cadeia</dt>
          <dd className="text-content qc-tabular">{event.chainSeq}</dd>
          <dt className="text-content-subtle">Hash do evento</dt>
          <dd className="break-all font-mono text-2xs text-content">{event.hash}</dd>
          <dt className="text-content-subtle">Hash anterior</dt>
          <dd className="break-all font-mono text-2xs text-content">{event.prevHash ?? '—'}</dd>
        </dl>

        <section aria-labelledby="diff-heading">
          <h3 id="diff-heading" className="mb-2 text-sm font-semibold text-content">
            Alterações registradas ({changed.length})
          </h3>

          {changed.length === 0 ? (
            <p className="rounded-md border border-border bg-surface-sunken px-3 py-3 text-sm text-content-muted">
              Este evento não registra alteração de valores. Ações de leitura, download e exportação
              são auditadas sem diferença de conteúdo.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full border-collapse text-sm">
                <caption className="qc-sr-only">
                  Comparação entre o valor anterior e o valor novo de cada campo alterado.
                </caption>
                <thead>
                  <tr className="border-b border-border bg-surface-sunken">
                    <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted">
                      Campo
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted">
                      Valor anterior
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted">
                      Valor novo
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {changed.map((row) => (
                    <tr key={row.field} className="border-b border-border-subtle last:border-b-0">
                      <th scope="row" className="px-3 py-2 text-left font-mono text-xs font-normal text-content">
                        {row.field}
                      </th>
                      <td className="px-3 py-2">
                        {/* O tipo de mudança vai em TEXTO, além do fundo colorido. */}
                        <span className="qc-sr-only">
                          {row.kind === 'added' ? 'Campo adicionado. ' : ''}
                        </span>
                        <span
                          className={cn(
                            'block whitespace-pre-wrap break-words rounded px-1.5 py-0.5 font-mono text-xs',
                            row.kind !== 'added' && 'bg-danger-subtle text-danger-content',
                          )}
                        >
                          {row.kind === 'added' ? '—' : row.before}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="qc-sr-only">
                          {row.kind === 'removed' ? 'Campo removido. ' : ''}
                        </span>
                        <span
                          className={cn(
                            'block whitespace-pre-wrap break-words rounded px-1.5 py-0.5 font-mono text-xs',
                            row.kind !== 'removed' && 'bg-success-subtle text-success-content',
                          )}
                        >
                          {row.kind === 'removed' ? '—' : row.after}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </Dialog>
  );
}

function buildDiff(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): DiffRow[] {
  const keys = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])].sort();

  return keys.map((key) => {
    const hasBefore = before !== null && key in before;
    const hasAfter = after !== null && key in after;
    const beforeText = hasBefore ? stringify(before?.[key]) : '';
    const afterText = hasAfter ? stringify(after?.[key]) : '';

    let kind: DiffRow['kind'] = 'unchanged';
    if (!hasBefore && hasAfter) kind = 'added';
    else if (hasBefore && !hasAfter) kind = 'removed';
    else if (beforeText !== afterText) kind = 'changed';

    return { field: key, before: beforeText, after: afterText, kind };
  });
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value, null, 2);
}

function actionTone(action: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  if (action === AuditAction.SIGN || action === AuditAction.APPROVE) return 'success';
  if (action === AuditAction.DELETE || action === AuditAction.REJECT) return 'danger';
  if (
    action === AuditAction.LOGIN_FAILED ||
    action === AuditAction.LOCKOUT ||
    action === AuditAction.VENDOR_ACCESS
  ) {
    return 'warning';
  }
  // UNLOCK é ato ADMINISTRATIVO de reabertura de acesso, com autor e
  // justificativa — não é o bloqueio automático de §11.300(d) e não deve
  // compartilhar o tom de alerta dele. Antes os dois saíam como `LOCKOUT` e
  // ficavam visualmente indistinguíveis na trilha.
  if (action === AuditAction.CREATE || action === AuditAction.UPDATE || action === AuditAction.UNLOCK) {
    return 'info';
  }
  return 'neutral';
}

function toIsoDate(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}
